import { observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Pagination } from '@mui/material';
import EmptyData, { EmptyDataProps } from 'components/EmptyData';
import MediaTypography from 'components/MediaTypography';
import store from 'store';

import lessonDefaultCover from '@common/assets/images/lessonDefaultCover.png';
import { ROLE, checkRole } from '@common/components/RBAC';
import { useServiceRouter } from '@common/components/ServiceRouter';
import { getRoute } from '@common/constants/CampusRoutes';
import canParentOpenProgramLesson from '@common/helpers/canParentOpenProgramLesson';
import useId from '@common/helpers/useId';
import { IStudentProgramModel } from '@common/models/StudentPrograms/StudentProgram';
import { IStudentProgramLessonModel } from '@common/models/StudentPrograms/StudentProgramLesson';

import LessonStatus from './Status';
import {
	ProgramLessonList,
	ProgramLessonListWrap,
	StyledLessonItemImage,
	StyledLessonItemInnerWrap,
	StyledLessonItemWrap,
} from './style';

interface ProgramLessonsSectionProps {
	programModel: IStudentProgramModel;
	lessonsKey: keyof IStudentProgramModel['lessons'];
	page: number;
	setPage: (value: number) => void;
}

const ON_PAGE_COUNT = 10;

function ProgramLessonsSection({
	programModel,
	lessonsKey,
	page,
	setPage,
}: ProgramLessonsSectionProps) {
	const { t } = useTranslation();
	const { programId, groupId, programGroupId } = useId(['programId', 'groupId', 'programGroupId']);
	const navigate = useNavigate();
	const { checkService } = useServiceRouter();
	const lessons = programModel.lessons[lessonsKey] as IStudentProgramLessonModel[];
	const isDemoUser = checkRole(ROLE.DEMO_TIS);
	const isParent = checkRole(ROLE.PARENT);

	const lessonsToShow = lessons.slice((page - 1) * ON_PAGE_COUNT, page * ON_PAGE_COUNT);

	if (!lessons || lessons.length <= 0) {
		let emptyDataProps: EmptyDataProps;
		switch (lessonsKey) {
			case 'on_checking':
				emptyDataProps = {
					title: t('program.checkingEmptyLessonsTitle'),
					subTitle: t('program.checkingEmptyLessonsDescription'),
				};
				break;
			case 'active': {
				emptyDataProps = {
					title: t('program.activeEmptyLessonsTitle'),
					subTitle: t('program.activeEmptyLessonsDescription'),
				};
				break;
			}
			case 'reset': {
				emptyDataProps = {
					title: t('program.resetEmptyLessonsTitle'),
					subTitle: t('program.resetEmptyLessonsDescription'),
				};
				break;
			}
			case 'completed':
				emptyDataProps = {
					title: t('program.completedEmptyLessonsTitle'),
					subTitle: t('program.completedEmptyLessonsDescription'),
				};
				break;
			default:
				emptyDataProps = {
					subTitle: t('shared.emptyDataSubTitle'),
				};
		}

		return <EmptyData {...emptyDataProps} />;
	}

	return (
		<ProgramLessonListWrap>
			<ProgramLessonList>
				{lessonsToShow.map((lesson, index) => {
					const canOpenLesson =
						store.isSelfStudentView ||
						canParentOpenProgramLesson({
							isAcademyService: checkService('academy'),
							isParent,
							lessonStatus: lesson.status,
							lessonsKey,
						});

					return (
						<StyledLessonItemWrap
							key={index}
							elevation={2}
							onClick={() => {
								canOpenLesson &&
									navigate(
										getRoute(
											['PROGRAMS'],
											programId,
											groupId,
											programGroupId,
											lesson.schedule_lesson_id,
										),
									);
							}}
							aria-disabled={!canOpenLesson}
						>
							<StyledLessonItemImage
								src={lesson.lesson_image || lessonDefaultCover}
								alt="Lesson image"
							/>
							<StyledLessonItemInnerWrap>
								<MediaTypography
									variant="body1"
									variants={{ sm: 'body2' }}
									lineClamp={{ sm: 2 }}
									sx={{ flex: 1 }}
								>
									{lesson.schedule_lesson_name || lesson.lesson_name}
								</MediaTypography>
							</StyledLessonItemInnerWrap>
							{!isDemoUser && <LessonStatus lesson={lesson} lessonsKey={lessonsKey} />}
						</StyledLessonItemWrap>
					);
				})}
			</ProgramLessonList>
			<Pagination
				page={page}
				onChange={(_, value) => setPage(value)}
				count={Math.ceil(lessons.length / ON_PAGE_COUNT)}
				sx={{ marginTop: '16px' }}
			/>
		</ProgramLessonListWrap>
	);
}

export default observer(ProgramLessonsSection);
